import { t } from "i18next";
import * as z from "zod";

import {
  isValidRoutableEmail,
  normalizeEmail,
} from "@/core/utils/func/input-normalization";

/**
 * Schéma du formulaire de création du compte Keycloak (`Users/add`, guide
 * v1.3) — étape 1 du parcours d'inscription : 5 champs + confirmation de
 * mot de passe (règle de correspondance).
 *
 * Factory (règle projet) : créé via `createKeycloakUserSchema()` et mémoïsé
 * au niveau composant sur la langue courante — jamais d'instance exportée.
 */

/** Schéma des champs seuls (ZodObject) — validation champ par champ via `.shape`. */
export const createKeycloakUserFieldsSchema = () =>
  z.object({
    username: z
      .string()
      .trim()
      .min(1, { message: t("requiredFieldError") })
      .transform((value) => value.toLowerCase()),
    email: z
      .string()
      .transform((raw) => {
        const trimmed = raw.trim();
        return trimmed ? normalizeEmail(trimmed) : "";
      })
      .refine((value) => value !== "", { message: t("requiredFieldError") })
      .refine((value) => value === "" || isValidRoutableEmail(value), {
        message: t("invalidEmailError"),
      }),
    firstName: z.string().trim().min(1, { message: t("requiredFieldError") }),
    lastName: z.string().trim().min(1, { message: t("requiredFieldError") }),
    // La politique détaillée est appliquée côté serveur (400 → message backend).
    password: z
      .string()
      .min(8, { message: t("passwordMinLengthError", { length: 8 }) }),
    confirmPassword: z.string(),
  });

/** Schéma complet — ajoute la correspondance mot de passe / confirmation. */
export const createKeycloakUserSchema = () =>
  createKeycloakUserFieldsSchema().refine(
    (values) => values.password === values.confirmPassword,
    {
      message: t("kyc.onboarding.keycloakUser.passwordMismatch"),
      path: ["confirmPassword"],
    }
  );

export type KeycloakUserFormValues = z.infer<
  ReturnType<typeof createKeycloakUserFieldsSchema>
>;
